// ==============================================================================================
// LA PLACE QUI RESTE, PAR HÔTE — déposée par un agent deux fois par jour, jamais sondée à l'écran.
//
// POURQUOI PAR HÔTE ET PLUS PAR SERVICE (refonte 2026-09-02). L'espace disque est une propriété d'un
// SYSTÈME DE FICHIERS, pas d'un service. La version précédente interrogeait chaque service sur
// `/disk` : elle ne pouvait donc RIEN dire des quatre machines de la flotte qui n'hébergent aucun de
// nos services — dont `srv-cldb` (les bases magasin) et `srv-objects` (les médias, la machine qui a
// saturé le 2026-07-18). Et sur les six autres, elle mesurait la racine du CONTENEUR : deux services
// co-localisés affichaient le même chiffre sous deux libellés différents.
//
// ⚠️ TROIS ÉTATS SE DISTINGUENT, et les confondre est exactement le défaut qu'on répare :
//   · un volume MESURÉ — sa jauge, son chemin, son heure ;
//   · un volume NON MESURÉ — son MOTIF, jamais une jauge à vide (qui se lirait « plein » ou « sain »
//     selon l'humeur du lecteur, et les deux seraient faux) ;
//   · un hôte QUI N'A JAMAIS PARLÉ — dit comme tel, avec quoi faire. L'omettre ferait lire un écran
//     complet là où il manque une machine, et la machine manquante est celle dont l'agent est tombé.
//
// ⚠️ CHAQUE LIGNE PORTE SON POINT DE MONTAGE ET SON HEURE. Le montage, parce qu'une jauge qui
// regarderait la racine système au lieu du volume de données serait fausse sans le dire. L'heure,
// parce qu'à deux relevés par jour un chiffre peut avoir douze heures — et « 12 Go libres » d'hier
// ne dit rien d'aujourd'hui.
// ==============================================================================================
import { useEffect, useState } from 'react';
import { EmptyState } from '@orgazone/ui-web';
import { api, type DiskState, type DiskHost, type DiskVolume } from '../api';

/**
 * Octets → « 412 Go ». Base 1000, comme `df -h` et comme les fabricants de disques : afficher des
 * Gio ici et des Go dans le terminal ferait douter du chiffre à chaque comparaison.
 */
function fmtOctets(n: number): string {
  const u = ['o', 'ko', 'Mo', 'Go', 'To'];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < u.length - 1) { v /= 1000; i += 1; }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

/** Paliers d'occupation : vert < 75 % · orange 75–90 · rouge > 90. Un volume à 90 % se remplit vite. */
function tonDe(mesure: boolean, utilise: number): string {
  if (!mesure) return 'var(--ink300)';
  if (utilise > 0.9) return 'var(--da-fg)';
  if (utilise > 0.75) return 'var(--wa-fg)';
  return 'var(--ok-fg)';
}

/** Un volume : la jauge quand c'est mesuré, le motif sinon. */
function Volume({ v }: { v: DiskVolume }) {
  const mesure = v.freeBytes != null && v.totalBytes != null && v.totalBytes > 0;
  const utilise = mesure ? 1 - v.freeBytes! / v.totalBytes! : 0;
  const ton = tonDe(mesure, utilise);
  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        {/* Le point de montage EN CLAIR : sans lui, personne ne sait quel volume a été regardé. */}
        <span className="mono" style={{ fontWeight: 700 }}>{v.mount}</span>
        {v.fsType && (
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>{v.fsType}</span>
        )}
        {v.device && (
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>{v.device}</span>
        )}
      </div>
      {mesure ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 20, fontWeight: 800, color: ton }}>{fmtOctets(v.freeBytes!)}</span>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>libres sur {fmtOctets(v.totalBytes!)}</span>
            <div style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 'var(--t-meta)', color: ton }}>{Math.round(utilise * 100)} % utilisé</span>
          </div>
          <div style={{ position: 'relative', height: 7, borderRadius: 'var(--r-r4)', background: 'var(--ink100)', marginTop: 8, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${Math.min(100, Math.round(utilise * 100))}%`, background: ton, borderRadius: 'var(--r-r4)' }} />
          </div>
        </>
      ) : (
        // ⚠️ Le MOTIF RÉEL, jamais une jauge vide qui se lirait « disque plein » ou « tout va bien ».
        <div className="mono" style={{ marginTop: 6, fontSize: 'var(--t-meta)', color: 'var(--wa-fg)' }}>
          non mesuré — {v.error ?? 'motif inconnu'}
        </div>
      )}
    </div>
  );
}

/** Un hôte et ses volumes — ou son silence, dit comme tel. */
function Hote({ h, heures }: { h: DiskHost; heures: number }) {
  return (
    <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-ctrl)', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: h.volumes.length ? 10 : 0 }}>
        <span style={{ fontWeight: 800 }}>{h.host}</span>
        {/* ⚠️ 🔴 UN AGENT PÉRIMÉ RELÈVE DEUX FOIS PAR JOUR SANS ÉCHOUER — il fait son travail,
            l'ancien, et rien ne le distingue d'un agent à jour (Forme T). Même mesure que côté
            sauvegardes : quand un contrôle existe dans un sens, son absence dans l'autre est un
            manque, pas une spécificité. */}
        {h.lastAt && h.agentStale && (
          <span
            className="mono"
            style={{
              fontSize: 'var(--t-micro)', fontWeight: 700, padding: '2px 7px', borderRadius: 5,
              background: 'var(--wa-bg)', color: 'var(--wa-fg)',
            }}
          >
            {h.agentVersion ? `agent v${h.agentVersion}` : 'agent sans version'} — À METTRE À JOUR
          </span>
        )}
        <div style={{ flex: 1 }} />
        {h.lastAt ? (
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: h.stale ? 'var(--wa-fg)' : 'var(--ink500)' }}>
            {h.stale ? 'dernier relevé ' : 'relevé le '}{new Date(h.lastAt).toLocaleString('fr-FR')}
          </span>
        ) : (
          <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--wa-fg)' }}>jamais relevé</span>
        )}
      </div>

      {/* ⚠️ UN HÔTE MUET DIT QUOI FAIRE. « Jamais relevé » sans la marche à suivre laisse un écran
          qui constate ; ici il envoie poser l'agent. */}
      {!h.volumes.length ? (
        <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
          Aucun relevé de cette machine. L’agent se pose avec
          {' '}<span style={{ color: 'var(--ink700)' }}>deploy/releve-disque.sh --installer</span>{' '}
          (configuration dans /etc/orgazone-disque.env).
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {h.volumes.map((v) => <Volume key={`${h.host}|${v.mount}`} v={v} />)}
        </div>
      )}

      {/* ⚠️ UN AGENT MUET DEPUIS PLUS D'UN JOUR SE DIT, même si les chiffres sont encore là : une
          jauge périmée a exactement l'air d'une jauge à jour. */}
      {h.stale && (
        <div className="mono" style={{ marginTop: 8, fontSize: 'var(--t-meta)', color: 'var(--wa-fg)' }}>
          Aucun relevé depuis plus de {heures * 2} h — l’agent de cette machine est probablement arrêté.
          Les chiffres ci-dessus sont ceux du dernier passage.
        </div>
      )}
    </div>
  );
}

export function DiskPanel() {
  const [state, setState] = useState<DiskState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // ⚠️ UNE SEULE LECTURE À L'OUVERTURE, sans minuterie : la donnée ne bouge que deux fois par jour.
    //    Rafraîchir toutes les 30 s comme la sonde de latence relirait 2 880 fois le même chiffre.
    api.healthDisk()
      .then((r) => { if (alive) { setState(r); setErr(null); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  const heures = state ? Math.round(state.intervalMs / 3_600_000) : 12;

  return (
    <div className="dcard" style={{ marginTop: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Espace disque</h2>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>
          déposé par chaque machine toutes les {heures} h — un suivi, pas du temps réel
        </span>
      </div>

      {err && <div className="err">État du disque illisible : {err}</div>}
      {!state && !err && <div className="muted">Lecture…</div>}

      {/* ⚠️ « AUCUNE MACHINE » N'EST PAS « TOUT VA BIEN » : sans cette distinction, un suivi jamais
          démarré se lirait comme un parc sain. On dit ce qui manque, et où le poser. */}
      {state && state.hosts.length === 0 && (
        <EmptyState
          icon="pulse"
          title="Aucune machine ne dépose de relevé"
          note="Chaque serveur pose son agent avec deploy/releve-disque.sh --installer. Les machines ATTENDUES se déclarent côté Console par DISK_EXPECTED_HOSTS — sans cette liste, une machine dont l’agent n’a jamais parlé n’apparaîtrait nulle part."
        />
      )}

      {state && state.hosts.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {state.hosts.map((h) => <Hote key={h.host} h={h} heures={heures} />)}
        </div>
      )}
    </div>
  );
}
